import { useMemo, useState } from "react";

import type { MasterControlUser } from "@/api/executive";
import { setBillingExempt } from "@/api/executive";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useToast } from "@/components/ToastProvider";
import { cn } from "@/lib/utils";

function getInitials(user: MasterControlUser): string {
  const base = user.name || user.email || user.id;
  if (!base) return "?";
  return base
    .split(/\s+/)
    .map((segment) => segment.slice(0, 1))
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function resolveStatusTone(status?: string | null): string {
  const normalized = (status ?? "").toString().toLowerCase();
  if (normalized.includes("suspend") || normalized.includes("disabled")) {
    return "bg-rose-500";
  }
  if (normalized.includes("pending") || normalized.includes("invited")) {
    return "bg-amber-400";
  }
  return "bg-emerald-400";
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatCount(value?: number | null): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-US");
}

export type MCUsersTableProps = {
  users: MasterControlUser[];
  loading?: boolean;
};

export function MCUsersTable({ users, loading }: MCUsersTableProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<MasterControlUser | null>(null);
  const [billingOverrides, setBillingOverrides] = useState<Record<string, boolean>>({});
  const [billingUpdating, setBillingUpdating] = useState(false);
  const { push } = useToast();

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const actionsA = a.agent_actions_24h ?? 0;
      const actionsB = b.agent_actions_24h ?? 0;
      if (actionsA === actionsB) {
        return (a.name ?? "").localeCompare(b.name ?? "");
      }
      return actionsB - actionsA;
    });
  }, [users]);

  const resolveOrgBillingExempt = (user?: MasterControlUser | null): boolean | null => {
    if (!user) return null;
    const orgId = user.organization_id ?? user.organization?.id ?? "";
    if (orgId && orgId in billingOverrides) {
      return billingOverrides[orgId];
    }
    const direct = user.organization_billing_exempt ?? user.organization?.billing_exempt;
    return typeof direct === "boolean" ? direct : null;
  };

  const handleBillingToggle = async (user?: MasterControlUser | null) => {
    if (!user) return;
    const orgId = user.organization_id ?? user.organization?.id ?? "";
    if (!orgId) return;
    const current = resolveOrgBillingExempt(user);
    const nextValue = current === null ? true : !current;
    setBillingUpdating(true);
    try {
      const result = await setBillingExempt({ org_id: orgId, billing_exempt: nextValue });
      setBillingOverrides((prev) => ({ ...prev, [orgId]: result.billing_exempt }));
      push({
        title: "Billing",
        message: result.billing_exempt
          ? "Billing exemption enabled for this organization."
          : "Billing exemption removed for this organization.",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to update billing exemption.";
      push({ title: "Billing", message });
    } finally {
      setBillingUpdating(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">System users</CardTitle>
          <p className="text-sm text-muted-foreground">Full roster with active usage signals.</p>
        </div>
        <div className="text-xs text-muted-foreground">{loading ? "Refreshing…" : `${users.length} accounts`}</div>
      </CardHeader>
      <CardContent className="overflow-hidden">
        <div className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead className="text-right">Agent actions (24h)</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 overflow-hidden rounded-full border border-border bg-background/40">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt={user.name ?? user.email ?? user.id} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-foreground/70">
                            {getInitials(user)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          <span>{user.name ?? "Unknown user"}</span>
                          <span className={cn("h-2 w-2 rounded-full", resolveStatusTone(user.status))} />
                        </div>
                        <p className="text-xs text-muted-foreground">{user.id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-foreground/80">{user.email ?? "—"}</TableCell>
                  <TableCell className="capitalize text-sm text-foreground/80">{user.role ?? "—"}</TableCell>
                  <TableCell className="text-sm text-foreground/80">{user.organization_name ?? user.organization?.name ?? "—"}</TableCell>
                  <TableCell className="text-sm text-foreground/80">{formatDate(user.last_seen_at ?? user.last_login_at)}</TableCell>
                  <TableCell className="text-right font-semibold text-foreground">
                    {formatCount(user.agent_actions_24h ?? user.agent_actions_total)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Drawer open={open && selected?.id === user.id} onOpenChange={(next) => (next ? (setSelected(user), setOpen(true)) : setOpen(false))}>
                      <DrawerTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelected(user);
                            setOpen(true);
                          }}
                        >
                          Open user
                        </Button>
                      </DrawerTrigger>
                      <DrawerContent className="bg-background text-foreground">
                        <DrawerHeader>
                          <DrawerTitle>{selected?.name ?? "User detail"}</DrawerTitle>
                          <DrawerDescription className="text-muted-foreground">
                            Review recent activity, organization membership, and impersonation eligibility.
                          </DrawerDescription>
                        </DrawerHeader>
                        <div className="space-y-4 border-t border-border px-6 py-4 text-sm text-foreground/80">
                          <dl className="grid gap-4 md:grid-cols-2">
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Email</dt>
                              <dd>{selected?.email ?? "—"}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Role</dt>
                              <dd className="capitalize">{selected?.role ?? "—"}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Organization</dt>
                              <dd>{selected?.organization_name ?? selected?.organization?.name ?? "—"}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Status</dt>
                              <dd>{selected?.status ?? "—"}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Billing exempt</dt>
                              <dd>
                                {resolveOrgBillingExempt(selected) === null
                                  ? "—"
                                  : resolveOrgBillingExempt(selected)
                                  ? "Enabled"
                                  : "Disabled"}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Last seen</dt>
                              <dd>{formatDate(selected?.last_seen_at ?? selected?.last_login_at)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Agent actions (24h)</dt>
                              <dd>{formatCount(selected?.agent_actions_24h)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">Total agent actions</dt>
                              <dd>{formatCount(selected?.agent_actions_total)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs uppercase tracking-wide text-muted-foreground">User ID</dt>
                              <dd>{selected?.id}</dd>
                            </div>
                          </dl>
                          <div className="flex flex-wrap gap-3">
                            <Button
                              type="button"
                              variant={resolveOrgBillingExempt(selected) ? "secondary" : "default"}
                              disabled={!selected?.organization_id && !selected?.organization?.id || billingUpdating}
                              onClick={() => handleBillingToggle(selected)}
                            >
                              {billingUpdating
                                ? "Updating…"
                                : resolveOrgBillingExempt(selected)
                                ? "Revoke billing exempt"
                                : "Grant billing exempt"}
                            </Button>
                            {!selected?.organization_id && !selected?.organization?.id && (
                              <p className="text-xs text-muted-foreground">
                                No organization linked to this user.
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Raw payload</p>
                            <pre className="mt-2 max-h-64 overflow-auto rounded-xl border border-border bg-background p-4 text-xs text-foreground/70">
                              {JSON.stringify(selected ?? {}, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </DrawerContent>
                    </Drawer>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && sortedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default MCUsersTable;
